import { MultipleButtonFloating } from "@/components";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import useCaseMutation from "@/hooks/useCaseMutation";
import { today } from "@/libraries/date";
import { DataIdSchema, PatientVisitFormSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { createFileRoute } from "@tanstack/react-router";
import omit from "lodash/omit";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute(
	"/dashboard/case/view/$id/patient-visits/create",
)({
	params: {
		parse: (params) => ({
			id: DataIdSchema.parse(Number(params.id)),
		}),
		stringify: ({ id }) => ({ id: id.toString() }),
	},
	component: RouteComponent,
});

function RouteComponent() {
	const caseId = Route.useParams({
		select: (s) => s.id,
	});
	const onBack = useBackward();
	const {
		control,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<PatientVisitFormSchema>({
		mode: "all",
		resolver: zodResolver(PatientVisitFormSchema),
		defaultValues: {
			caseId,
			location: "",
			department: "",
			seen_by: "",
		},
	});

	const { addPatientVisit } = useCaseMutation();

	const onSubmit: SubmitHandler<PatientVisitFormSchema> = (data, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		addPatientVisit.mutate(data, {
			onSuccess: () => {
				toast.success("Patient’s visit added");
			},
		});
		return onBack();
	};

	return (
		<>
			<BackAppBar title="Add Patient’s Visit" />
			<form onSubmit={handleSubmit(onSubmit)} className=" bg-white h-full">
				<div className="container flex flex-col gap-4 py-4">
					<Controller
						control={control}
						name="date"
						render={({ field }) => (
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<DatePicker
									label="Date"
									className="[&_.MuiInputBase-root]:!rounded-xl"
									format="DD/MM/YYYY"
									disableFuture={true}
									maxDate={today()}
									slots={{
										openPickerIcon: CalendarTodayIcon,
									}}
									{...omit(field, ["ref"])}
								/>
							</LocalizationProvider>
						)}
					/>
					<Controller
						control={control}
						name="time"
						render={({ field }) => (
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<TimePicker
									label="Time"
									className="[&_.MuiInputBase-root]:!rounded-xl"
									{...omit(field, ["ref"])}
								/>
							</LocalizationProvider>
						)}
					/>
					<Controller
						control={control}
						name="location"
						render={({ field, fieldState }) => (
							<TextField
								label="Location"
								placeholder="Enter Location"
								className="[&_.MuiInputBase-root]:!rounded-xl"
								error={!!fieldState.error?.message}
								{...omit(field, ["ref"])}
							/>
						)}
					/>
					<Controller
						control={control}
						name="department"
						render={({ field, fieldState }) => (
							<TextField
								label="Department"
								placeholder="Enter Department"
								className="[&_.MuiInputBase-root]:!rounded-xl"
								error={!!fieldState.error?.message}
								{...omit(field, ["ref"])}
							/>
						)}
					/>
					<Controller
						control={control}
						name="seen_by"
						render={({ field, fieldState }) => (
							<TextField
								label="Seen by"
								placeholder="Enter Seen by"
								className="[&_.MuiInputBase-root]:!rounded-xl"
								error={!!fieldState.error?.message}
								{...omit(field, ["ref"])}
							/>
						)}
					/>
				</div>
				<MultipleButtonFloating
					negativeButtonProps={{
						label: "Cancel",
						type: "button",
						onClick: () => onBack(),
					}}
					positiveButtonProps={{
						label: "Submit",
						type: "submit",
						disabled: !isDirty || !isValid || isSubmitting,
					}}
				/>
			</form>
		</>
	);
}
